import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import PrivateRoute from './components/PrivateRoute';
import SpecialUserRoute from './components/SpecialUserRoute';
import AppShell from './components/AppShell';
import AdminRoute from './components/AdminRoute';
import LocationSender from './components/LocationSender';
import { useNotifications } from './context/NotificationContext';
import { EventDetailsDialog } from './components/EventDetailsDialog';
import { useAuth } from './context/AuthContext';

// Страницы грузятся лениво — каждая уходит в свой chunk,
// тяжёлые зависимости (chart.js, leaflet, pdf.js) не попадают в основной бандл.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MapPage = lazy(() => import('./pages/MapPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const InventoryDashboardPage = lazy(() => import('./pages/InventoryDashboardPage'));
const LiveMapPage = lazy(() => import('./pages/LiveMapPage'));
const SendLocationPage = lazy(() => import('./pages/SendLocationPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DoctorsPage = lazy(() => import('./pages/DoctorsPage'));
const PharmaciesPage = lazy(() => import('./pages/PharmaciesPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const SalesAnalyticsPage = lazy(() => import('./pages/SalesAnalyticsPage'));
const SalesDashboardPage = lazy(() => import('./pages/SalesDashboardPage'));
const SpecialitiesPage = lazy(() => import('./pages/SpecialitiesPage'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

function App() {
  const { user } = useAuth();
  const { eventDetailsToShow, setEventDetailsToShow } = useNotifications();

  return (
    <Router>
      {user && <LocationSender />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Публичные экраны — без каркаса */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<SpecialUserRoute><Register /></SpecialUserRoute>} />

          {/* Приложение — внутри общего каркаса (боковая панель + шапка) */}
          <Route element={<AppShell />}>
            <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/catalog" element={<PrivateRoute><CatalogPage /></PrivateRoute>} />
            <Route path="/map" element={<PrivateRoute><MapPage /></PrivateRoute>} />
            <Route path="/calendar" element={<PrivateRoute><CalendarPage /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
            <Route path="/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            <Route path="/inventory" element={<PrivateRoute><InventoryDashboardPage /></PrivateRoute>} />
            <Route path="/send-location" element={<PrivateRoute><SendLocationPage /></PrivateRoute>} />
            <Route path="/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
            <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
            <Route path="/doctors" element={<PrivateRoute><DoctorsPage /></PrivateRoute>} />
            <Route path="/pharmacies" element={<PrivateRoute><PharmaciesPage /></PrivateRoute>} />
            <Route path="/reps-map" element={<AdminRoute><LiveMapPage /></AdminRoute>} />
            <Route path="/import" element={<AdminRoute><ImportPage /></AdminRoute>} />
            <Route path="/specialities" element={<AdminRoute><SpecialitiesPage /></AdminRoute>} />
            <Route path="/analytics/sales-lite" element={<AdminRoute><SalesAnalyticsPage /></AdminRoute>} />
            <Route path="/analytics/sales" element={<AdminRoute><SalesDashboardPage /></AdminRoute>} />
          </Route>
        </Routes>
      </Suspense>

      <EventDetailsDialog
        event={eventDetailsToShow}
        onClose={() => setEventDetailsToShow(null)}
      />
    </Router>
  );
}

export default App;
